/**
 * Botão de atualizar uma lista.
 *
 * Existe porque as ações destas telas são ASSÍNCRONAS: a tela publica o pedido, quem muda o
 * estado é um consumidor do outro lado, e refazer a busca no instante do clique traz o dado de
 * antes. Recarregar sozinho depois de um tempo seria adivinhar quanto tempo — o botão devolve
 * essa decisão a quem está olhando.
 *
 * O idioma já existia copiado à mão em dois modais, cada um sem estado de carregamento. Um átomo
 * só mantém os cinco lugares iguais e resolve o duplo clique disparando duas buscas.
 *
 * `busy` é OPCIONAL de propósito: no modal quem carrega é o próprio componente e o flag existe;
 * na página o `PageList` é o dono do `useFetch` e já mostra o seu `<Loading />`, então não há flag
 * para passar — e inventar um giro que não acompanha a busca de verdade mentiria sobre o estado.
 */
import { FaSyncAlt } from 'react-icons/fa';

interface Props {
    onClick: () => void;
    /** girando e desabilitado enquanto a busca está em voo */
    busy?: boolean;
    /**
     * O que está sendo atualizado, para o rótulo acessível distinguir os botões.
     *
     * Numa tela de Títulos com o modal de faturas aberto existem DOIS botões de atualizar ao
     * mesmo tempo; "Atualizar" sozinho não diz qual é qual para quem usa leitor de tela nem para
     * o teste.
     */
    label: string;
    /** `link` para dentro de modal (texto discreto); `icon` ao lado dos controles da página */
    variant?: 'link' | 'icon';
}

const LINK = 'inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline '
    + 'disabled:opacity-60 disabled:cursor-not-allowed disabled:no-underline';

// neutro, e não azul: ao lado de um botão de ação primária (Pagamentos Não Identificados,
// Registrar Pagamento) um segundo azul disputaria a atenção com ele
const ICON = 'p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 '
    + 'dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 '
    + 'disabled:opacity-60 disabled:cursor-not-allowed';

const RefreshButton: React.FC<Props> = ({ onClick, busy = false, label, variant = 'link' }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={busy}
        aria-label={`Atualizar ${label}`}
        title={`Atualizar ${label}`}
        className={variant === 'icon' ? ICON : LINK}
    >
        <FaSyncAlt className={busy ? 'animate-spin' : ''} size={variant === 'icon' ? 18 : undefined} />
        {variant === 'link' && <span>Atualizar</span>}
    </button>
);

export default RefreshButton;
